import { CartLinesUpdateEvent } from '@shopify/events';

class ShopSocialLoader extends HTMLElement {
  #baseAriaModal;
  #baseDialog;
  #baseInertRecords = [];
  #baseTrigger;
  #buyDialog;
  #buyInertRecords = [];
  #buyTrigger;
  #cartConfirmation;
  #dialogObserver;
  #intersectionObserver;
  #feedObserver;
  #feedFrame;
  #observedEntries = new WeakSet();
  #managedScrollLock = false;
  #pageScrollPosition;
  #pageScroller;
  #syncQueued = false;

  connectedCallback() {
    this.addEventListener('click', this.#rememberBaseTrigger, true);
    this.addEventListener('keydown', this.#rememberBaseTrigger, true);
    this.#dialogObserver = new MutationObserver(this.#queueDialogSync);
    this.#dialogObserver.observe(document.body, {
      attributeFilter: ['class', 'style'],
      attributes: true,
      childList: true,
      subtree: true,
    });
    window.addEventListener('keydown', this.#handleDialogKeydown, true);
    this.#syncDialogs();

    if ('IntersectionObserver' in window) {
      // The viewport root also clips against Horizon's nested desktop scroller.
      // Observe the cards themselves: the heading/preload margin can be visible
      // before Foursixty considers any of the media eligible to load.
      this.#intersectionObserver = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) this.#queueFeedSync();
      });
    } else {
      document.addEventListener('scroll', this.#handlePageScroll, true);
    }

    this.#feedObserver = new MutationObserver(this.#observeFeed);
    this.#feedObserver.observe(this, { childList: true, subtree: true });
    this.#observeFeed();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#rememberBaseTrigger, true);
    this.removeEventListener('keydown', this.#rememberBaseTrigger, true);
    window.removeEventListener('keydown', this.#handleDialogKeydown, true);
    this.#dialogObserver?.disconnect();
    this.#intersectionObserver?.disconnect();
    this.#feedObserver?.disconnect();
    document.removeEventListener('scroll', this.#handlePageScroll, true);
    cancelAnimationFrame(this.#feedFrame);
    this.#feedFrame = undefined;
    this.#observedEntries = new WeakSet();
    this.#teardownBuyDialog();
    this.#teardownBaseDialog();
  }

  #queueDialogSync = () => {
    if (this.#syncQueued) return;

    this.#syncQueued = true;
    requestAnimationFrame(() => {
      this.#syncQueued = false;
      this.#syncDialogs();
    });
  };

  #syncDialogs = () => {
    const baseDialog = document.querySelector(
      '.fs-slider_v2_6.fs-prepended-detail .fs-detail-outer-container'
    );

    if (baseDialog !== this.#baseDialog) {
      this.#teardownBuyDialog();
      this.#teardownBaseDialog();

      if (baseDialog) this.#setupBaseDialog(baseDialog);
    }

    if (baseDialog) {
      this.#suppressProviderBranding(baseDialog);
      this.#labelProductActions(baseDialog);
    }

    const buyDialog = baseDialog?.querySelector('.fs-buy-container:not(.fs-unslid)');

    if (buyDialog !== this.#buyDialog) {
      this.#teardownBuyDialog();

      if (buyDialog) this.#setupBuyDialog(buyDialog);
    }

    const cartConfirmation = buyDialog?.querySelector('.fs-added-notification');

    if (cartConfirmation !== this.#cartConfirmation) {
      this.#cartConfirmation = cartConfirmation;

      if (cartConfirmation) this.#syncHorizonCart();
    }
  };

  #setupBaseDialog = (dialog) => {
    this.#baseDialog = dialog;
    this.#baseTrigger ??= document.activeElement instanceof HTMLElement ? document.activeElement : undefined;

    const providerWrapper = dialog.closest('.fs-slider_v2_6.fs-prepended-detail');
    const backgroundElements = [...document.body.children].filter(
      (element) => element !== providerWrapper && !['SCRIPT', 'STYLE'].includes(element.tagName)
    );

    this.#pageScroller = document.querySelector('.page-wrapper');
    this.#pageScrollPosition = this.#pageScroller?.scrollTop;
    this.#baseInertRecords = this.#isolate(backgroundElements);

    if (!document.documentElement.hasAttribute('scroll-lock')) {
      document.documentElement.setAttribute('scroll-lock', '');
      this.#managedScrollLock = true;
    }

    requestAnimationFrame(() => {
      if (this.#baseDialog !== dialog || this.#buyDialog) return;

      dialog.querySelector('#fs-detail-close')?.focus({ preventScroll: true });
    });
  };

  #rememberBaseTrigger = (event) => {
    const trigger = event.target instanceof Element ? event.target.closest('.fs-timeline-entry[tabindex]') : null;

    if (trigger instanceof HTMLElement && this.contains(trigger)) this.#baseTrigger = trigger;
  };

  #teardownBaseDialog = () => {
    if (!this.#baseDialog) return;

    this.#restore(this.#baseInertRecords);
    this.#baseInertRecords = [];

    if (this.#managedScrollLock) {
      document.documentElement.removeAttribute('scroll-lock');
      this.#managedScrollLock = false;
    }

    this.#baseTrigger?.focus({ preventScroll: true });

    if (this.#pageScroller && this.#pageScrollPosition !== undefined) {
      this.#pageScroller.scrollTop = this.#pageScrollPosition;
    }

    this.#baseDialog = undefined;
    this.#baseTrigger = undefined;
    this.#pageScroller = undefined;
    this.#pageScrollPosition = undefined;
  };

  #setupBuyDialog = (dialog) => {
    this.#buyDialog = dialog;
    this.#buyTrigger = document.activeElement instanceof HTMLElement ? document.activeElement : undefined;
    this.#baseAriaModal = this.#baseDialog?.getAttribute('aria-modal');
    this.#baseDialog?.setAttribute('aria-modal', 'false');

    const backgroundElements = [...(dialog.parentElement?.children ?? [])].filter(
      (element) => element !== dialog
    );

    this.#buyInertRecords = this.#isolate(backgroundElements);
    dialog.setAttribute('aria-label', 'Choose product options');
    dialog.setAttribute('aria-modal', 'true');
    dialog.setAttribute('role', 'dialog');

    requestAnimationFrame(() => {
      if (this.#buyDialog !== dialog) return;

      dialog.querySelector('.fs-cancel-purchase')?.focus({ preventScroll: true });
    });
  };

  #labelProductActions = (dialog) => {
    dialog.querySelectorAll('button.fs-shopify-add-cart.fs-shopify-add-cart-container').forEach((button) => {
      if (button.textContent?.trim() === 'SIZE') return;

      const productName = button
        .closest('.fs-product-column')
        ?.querySelector('.fs-product-name')
        ?.textContent?.trim();

      button.textContent = 'SIZE';
      button.setAttribute('aria-label', productName ? `Choose a size for ${productName}` : 'Choose a size');
    });
  };

  #suppressProviderBranding = (dialog) => {
    const branding = dialog.querySelector('#fs-detail-branding');

    if (!(branding instanceof HTMLElement)) return;

    branding.hidden = true;
    branding.setAttribute('aria-hidden', 'true');
    branding.setAttribute('tabindex', '-1');

    if (branding.style.getPropertyValue('display') !== 'none' || branding.style.getPropertyPriority('display') !== 'important') {
      branding.style.setProperty('display', 'none', 'important');
    }
  };

  #teardownBuyDialog = () => {
    if (!this.#buyDialog) return;

    this.#restore(this.#buyInertRecords);
    this.#buyInertRecords = [];

    if (this.#baseDialog) {
      if (this.#baseAriaModal === null) {
        this.#baseDialog.removeAttribute('aria-modal');
      } else if (this.#baseAriaModal !== undefined) {
        this.#baseDialog.setAttribute('aria-modal', this.#baseAriaModal);
      }
    }

    this.#buyTrigger?.focus({ preventScroll: true });
    this.#buyDialog = undefined;
    this.#buyTrigger = undefined;
    this.#cartConfirmation = undefined;
    this.#baseAriaModal = undefined;
  };

  #syncHorizonCart = async () => {
    const cartItems = document.querySelector('cart-items-component[data-drawer]');

    if (typeof cartItems?.fetchCartData !== 'function') return;

    try {
      const ajaxCart = await cartItems.fetchCartData();
      const cart = CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart);

      if (!cart?.lines.length) return;

      const result = Promise.resolve({
        cart,
        detail: {
          itemCount: cart.totalQuantity,
          source: 'foursixty',
        },
      });

      this.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'update',
          context: 'standard-action',
          lines: cart.lines.map(({ id, quantity }) => ({ id, quantity })),
          promise: result,
        })
      );
    } catch (error) {
      console.warn('[shop-social] Could not refresh Horizon cart state.', error);
    }
  };

  #isolate = (elements) =>
    elements.map((element) => {
      const record = {
        ariaHidden: element.getAttribute('aria-hidden'),
        element,
        inert: element.inert,
      };

      element.inert = true;
      element.setAttribute('aria-hidden', 'true');

      return record;
    });

  #restore = (records) => {
    records.forEach(({ ariaHidden, element, inert }) => {
      element.inert = inert;

      if (ariaHidden === null) {
        element.removeAttribute('aria-hidden');
      } else {
        element.setAttribute('aria-hidden', ariaHidden);
      }
    });
  };

  #handleDialogKeydown = (event) => {
    const activeDialog = this.#buyDialog ?? this.#baseDialog;

    if (!activeDialog) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopImmediatePropagation();

      const closeControl = this.#buyDialog
        ? this.#buyDialog.querySelector('.fs-cancel-purchase, #fs-dismiss')
        : this.#baseDialog?.querySelector('#fs-detail-close');

      closeControl?.click();
      return;
    }

    if (event.key !== 'Tab') return;

    const focusable = [...activeDialog.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )].filter(
      (element) =>
        !element.closest('.fs-unslid') &&
        element.getClientRects().length > 0 &&
        element.getAttribute('aria-hidden') !== 'true' &&
        getComputedStyle(element).visibility !== 'hidden'
    );

    if (focusable.length === 0) {
      event.preventDefault();
      return;
    }

    const first = focusable[0];
    const last = focusable.at(-1);
    const focusIsOutside = !activeDialog.contains(document.activeElement);
    const shouldWrapBack = !event.shiftKey && document.activeElement === last;
    const shouldWrapForward = event.shiftKey && (document.activeElement === first || focusIsOutside);

    if (!focusIsOutside && !shouldWrapBack && !shouldWrapForward) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    (event.shiftKey ? last : first).focus({ preventScroll: true });
  };

  #observeFeed = () => {
    if (!this.isConnected) return;

    if (!this.#intersectionObserver) {
      this.#queueFeedSync();
      return;
    }

    // App responses, pagination and Theme Editor replacements can arrive later.
    this.querySelectorAll('.fs-timeline-entry').forEach((entry) => {
      if (this.#observedEntries.has(entry)) return;

      this.#observedEntries.add(entry);
      this.#intersectionObserver.observe(entry);
    });
  };

  #handlePageScroll = (event) => {
    if (event.target?.contains?.(this)) this.#queueFeedSync();
  };

  #queueFeedSync = () => {
    if (this.#feedFrame !== undefined) return;

    this.#feedFrame = requestAnimationFrame(() => {
      this.#feedFrame = undefined;
      if (!this.isConnected) return;

      // Foursixty's deferred loader listens to window/rail scroll, not Horizon's
      // page wrapper. Notify that existing loader without moving the user's rail
      // or taking ownership of provider media, pagination or commerce.
      this.querySelectorAll('.fs-wrapper.fs-slider_v2_6-feed').forEach((rail) => {
        rail.dispatchEvent(new Event('scroll'));
      });
    });
  };
}

if (!customElements.get('shop-social-loader')) {
  customElements.define('shop-social-loader', ShopSocialLoader);
}
